import { useQuery } from "react-query";
import { loggedInApiCall } from "../../axiosClient/userApi";
import { zodParseWithLog } from "../../apiErrorLogs";
import { TagType, TagContext } from "../../../types/tag";
import { z } from "zod";

// Schéma Zod pour TagType
const TagTypeSchema: z.ZodSchema<TagType> = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    description: z.string().optional(),
    context: z.enum(["global", "product", "ingredient", "cms", "supplier", "organization", "packaging"]),
    color: z.string(),
    active: z.boolean(),
    required: z.boolean(),
    multiple: z.boolean(),
    allowFreeValues: z.boolean().optional(), // Added for new tag system
    sortOrder: z.number(),
    metadata: z.record(z.string(), z.any()).optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    company: z
        .object({
            id: z.number(),
            name: z.string(),
        })
        .optional(),
    values: z.array(z.any()).optional(), // TagValue[] - éviter la référence circulaire
});

export interface TagTypeListParams {
    page?: number;
    itemsPerPage?: number;
    context?: TagContext;
    active?: boolean;
    includeInactive?: boolean;
    search?: string;
    order?: Record<string, "asc" | "desc">;
}

interface ApiResponse<T> {
    "hydra:member": T[];
    "hydra:totalItems": number;
    "hydra:view"?: {
        "hydra:first"?: string;
        "hydra:last"?: string;
        "hydra:next"?: string;
        "hydra:previous"?: string;
    };
}

export const useGetAllTagTypes = (params: TagTypeListParams = {}) => {
    const query = useQuery({
        queryKey: ["getAllTagTypes", params],
        queryFn: async (): Promise<ApiResponse<TagType>> => {
            const searchParams = new URLSearchParams();

            if (params.page) searchParams.append("page", params.page.toString());
            if (params.itemsPerPage) searchParams.append("itemsPerPage", params.itemsPerPage.toString());
            if (params.context) searchParams.append("context", params.context);
            if (params.active !== undefined) searchParams.append("active", params.active.toString());
            if (params.includeInactive !== undefined)
                searchParams.append("includeInactive", params.includeInactive.toString());
            if (params.search) searchParams.append("search", params.search);
            if (params.order) {
                Object.entries(params.order).forEach(([field, direction]) => {
                    searchParams.append(`order[${field}]`, direction);
                });
            }

            const response = await loggedInApiCall.get(`/api/tag_types?${searchParams.toString()}`);
            return response.data;
        },
        select: (data: ApiResponse<TagType>) => {
            const validatedMembers = data["hydra:member"].map((tagType) =>
                zodParseWithLog(tagType, TagTypeSchema, "getAllTagTypes"),
            );
            return {
                ...data,
                "hydra:member": validatedMembers,
            };
        },
    });

    return {
        tagTypes: query.data?.["hydra:member"],
        totalItems: query.data?.["hydra:totalItems"],
        ...query,
    };
};
